<template>
    <v-container class="space-y-lg pb-xl md:space-y-2xl">
        <div class="flex flex-col lg:mb-6 lg:flex-row-reverse lg:gap-8">
            <div class="flex w-full flex-col px-sm md:px-0">
                <v-h3 v-if="story.content.title"
                    tag="h1"
                    class="mb-5">
                    {{ story.content.title }}
                </v-h3>

                <!-- Idk if this was actually needed but incase it was it is included here AND Storyblok alike. -->
                <v-storyblok-component :is="story.content.active_campaigns[0]"
                    v-if="hasActiveCampaigns"
                    :story="story.content.active_campaigns"
                    :blok="story.content.active_campaigns[0]" />

                <div class="flex w-full flex-col gap-8 lg:flex-row">
                    <div class="relative order-2 w-full bg-cover lg:order-1 lg:rounded">
                        <span role="presentation"
                            class="absolute inset-0 rounded bg-black/20"></span>

                        <!-- <nuxt-img v-show="story.content.image?.filename"
                            :src="story.content.image.filename"
                            loading="lazy"
                            provider="storyblok"
                            class="aspect-video size-full object-cover"
                            :img-attrs="{
                                class: 'w-full h-full'
                            }"
                            sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
                            :placeholder="[300]"
                            :alt="story.content.image?.alt ?? story.name" /> -->

                        <nuxt-picture v-if="story.content.image?.filename"
                            :src="story.content.image.filename"
                            :alt="story.content.image?.alt ?? story.content.title"
                            provider="storyblok"
                            loading="lazy"
                            width="410"
                            height="205"
                            sizes="xs:100vw sm:640px md:768px lg:325px xl:410px 2xl:496px"
                            class=""
                            :placeholder="[300]"
                            :img-attrs="{
                                class: 'w-full h-full rounded object-cover aspect-[21/9]'
                            }" />

                        <nuxt-img v-show="story.content.logo?.filename"
                            class="absolute left-1/2 top-1/2 size-2/12 h-auto w-32
                            -translate-x-1/2 -translate-y-1/2 lg:h-auto lg:w-48"
                            :src="story.content.logo?.filename"
                            loading="lazy"
                            provider="storyblok"
                            width="128"
                            :placeholder="[300]"
                            sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px"
                            :alt="story.content.logo?.alt ?? story.name" />
                    </div>

                    <div class="order-1 w-full lg:order-2">
                        <!-- Old description content. This is preserved description for "old format" brand pages. -->
                        <div v-if="!story.content.description">
                            <div class="whitespace-pre-line text-justify font-sans
                                text-base font-normal">
                                <div :class="{
                                    // eslint-disable-next-line max-len
                                    'prose-5xs line-clamp-3 max-h-28 text-[color:var(--font-color)] md:line-clamp-6 lg:line-clamp-none lg:max-h-none': !showAll,
                                }">
                                    <p>
                                        {{ story.content.short_description }}
                                    </p>
                                </div>
                            </div>

                            <button v-if="!showAll"
                                type="button"
                                class="text-sm outline-none hover:underline focus:underline lg:hidden"
                                @click="showAll = true">
                                {{ t('read-more') }}
                            </button>
                        </div>

                        <!-- New rich text format description. -->
                        <sb-rich-text v-if="story.content.description"
                            :document="story.content.description"
                            class="prose-5xs w-full text-[color:var(--font-color)]" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Additional block content. -->
        <template v-for="component in story.content.additional_bloks"
            :key="component._uid">
            <v-storyblok-component :is="component"
                :blok="component"
                class="mx-sm md:mx-md" />
        </template>

        <!-- Product listing -->
        <v-elevate-product-list
            v-slot="{
                products,
                productCount,
                hasNextPage,
                loadMore,
                activeFiltersCount,
                clearFilter
            }"
            :campaign-only="isCampaignOnly"
            class="space-y-lg px-sm sm:px-0">
            <div class="flex flex-col justify-between">
                <p class="text-sm tracking-xl text-gray-500 md:block">
                    {{ productCount }} {{ t('amount-products') }}
                </p>

                <div class="mt-sm flex gap-lg lg:pt-md">
                    <v-button type="button"
                        theme="black"
                        size="sm"
                        class="flex items-center space-x-xs hover:underline"
                        @click="isModalOpen = true">
                        <template v-if="activeFiltersCount > 0">
                            <v-category-filter-count :active-filters-count="activeFiltersCount" />
                        </template>

                        <span>{{ t('filter-title') }}</span>

                        <v-icon icon="filter"
                            class="text-lg"></v-icon>
                    </v-button>

                    <v-button v-if="activeFiltersCount > 0"
                        theme="gray"
                        size="sm"
                        @click="clearFilter">
                        {{ t('filter-clear') }}
                    </v-button>
                </div>

                <v-elevate-common-facets class="mt-xs" />

                <v-category-order class="mt-md" />
            </div>

            <div class="grid grid-cols-2 gap-x-xs gap-y-lg
                md:grid-cols-3 md:gap-x-sm md:gap-y-xl lg:grid-cols-4">
                <template v-for="(product, index) in products"
                    :key="product.key">
                    <v-category-cta v-if="(index > 0 && index % 12 === 0) && ctaContent != null && isCtaVisible"
                        :content="ctaContent"
                        class="col-span-full" />

                    <v-elevate-product-card :product="product" />
                </template>
            </div>

            <div class="grid md:grid-cols-8">
                <div class="space-y-md md:col-span-2 md:col-start-4">
                    <v-button class="w-full"
                        :theme="hasNextPage ? 'black' : 'gray'"
                        :disabled="!hasNextPage"
                        :aria-label="hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded')"
                        @click="loadMore">
                        {{ hasNextPage ? $t('loader.show-more') : $t('loader.show-more-loaded') }}
                    </v-button>

                    <div class="space-y-xs">
                        <div class="h-[2px] rounded-full bg-gray-300">
                            <div class="h-[2px] rounded-full bg-black"
                                :style="{
                                    width: `${(products.length / productCount) * 100}%`
                                }"></div>
                        </div>

                        <p class="text-center">
                            {{ $t('loader.show-more-prefix') }} {{ products.length }}
                            / {{ productCount }} {{ $t('loader.show-more-suffix') }}
                        </p>
                    </div>

                    <button class="w-full text-base underline"
                        type="button"
                        @click="scrollToTop">
                        <span class="">{{ $t('loader.back-to-start') }}</span>
                    </button>
                </div>
            </div>

            <v-category-filter-modal :is-open="isModalOpen"
                :results="productCount"
                @close="isModalOpen = false" />
        </v-elevate-product-list>

        <!-- Content block for SEO purposes. -->
        <div v-if="story.content.content"
            class="space-y-sm break-words text-center text-gray-500">
            <div>
                <sb-rich-text :document="story.content.content"
                    class="prose-5xs" />
            </div>
        </div>
    </v-container>
</template>

<script setup lang="ts">
import type { ISbStoryData } from 'storyblok-js-client';
import type { ISbRichtext } from '@storyblok/js';
import type {
    BlokAsset,
    MetaFieldsPlugin,
    CmdProductListPlugin,
    BlokComponent,
    BlokColorpicker,
} from '~~/types';

const properties = defineProps<{
    story: ISbStoryData<BrandLander>
}>();

const { t } = useI18n();

const showAll = ref(false);

interface BrandLander {
    component: 'sb-category-lander'
    meta: MetaFieldsPlugin
    image: BlokAsset
    logo?: BlokAsset
    title: string
    description: ISbRichtext
    // eslint-disable-next-line @typescript-eslint/naming-convention
    short_description: string
    // eslint-disable-next-line @typescript-eslint/naming-convention
    category_menu: string[]
    content: ISbRichtext
    // eslint-disable-next-line @typescript-eslint/naming-convention
    description_color: BlokColorpicker
    // eslint-disable-next-line @typescript-eslint/naming-convention
    products: CmdProductListPlugin
    // eslint-disable-next-line @typescript-eslint/naming-convention
    campaign_only: boolean
    // eslint-disable-next-line @typescript-eslint/naming-convention
    additional_bloks: BlokComponent
    // eslint-disable-next-line @typescript-eslint/naming-convention
    active_campaigns: ISbStoryData[]
}

const ctaContent = useStoryblokStory('global/category-cta');

const categoryId = computed(() => properties.story.uuid);
const visibleOnPage = computed(() => ctaContent.value?.content.on_pages);

const isCtaVisible = computed(() => (
    visibleOnPage.value?.length && visibleOnPage.value.includes(categoryId.value)
));

const isModalOpen = ref(false);

const isCampaignOnly = computed(() => properties.story.content.campaign_only);

const hasActiveCampaigns = computed(() => properties.story.content.active_campaigns?.length > 0);

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

useSeoMeta({
    title: properties.story.content.title || properties.story.name,
    description: properties.story.content?.short_description ?? undefined,
    ogTitle: () => properties.story.content?.title ?? undefined,
    ogDescription: () => properties.story.content?.short_description ?? undefined,
    ogImage: () => properties.story.content?.image?.filename ?? undefined,
});

useDefaultDynamicHead({ includeQueryInCanonicalUrl: false });
</script>

<i18n lang="json">
{
    "et": {
        "amount-products": "toodet",
        "filter-title": "Filtrid",
        "read-more": "Loe rohkem",
        "show-less": "Näita vähem"
    },
    "fi": {
        "amount-products": "tuotetta",
        "filter-title": "Suodata",
        "read-more": "Lue lisää",
        "show-less": "Näytä vähemmän"
    }
}
</i18n>
